import React from 'react';
import {
  Dashboard as DashboardIcon,
  Domain as DomainIcon,
  People as PeopleIcon,
  Explore as ExploreIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  Apartment as ApartmentIcon,
  SupportAgent as SupportAgentIcon,
  ContactMail as ContactMailIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import { ManualSection, AppManualContent } from '../../Commun/components/UserManual';

// ─────────────────────────────────────────────────────────────────────────────
// CRM — Section definitions
// ─────────────────────────────────────────────────────────────────────────────

export const CRM_SECTIONS: ManualSection[] = [
  { id: 'dashboard',     icon: <DashboardIcon />,     color: '#0284c7' },
  { id: 'organisations', icon: <DomainIcon />,        color: '#0d9488' },
  { id: 'contacts',      icon: <PeopleIcon />,        color: '#6366f1' },
  { id: 'visits',        icon: <ExploreIcon />,       color: '#ea580c' },
  { id: 'cdc',           icon: <DescriptionIcon />,   color: '#7c3aed' },
  { id: 'opportunities', icon: <TrendingUpIcon />,    color: '#059669' },
  { id: 'chantiers',     icon: <ApartmentIcon />,     color: '#d97706' },
  { id: 'reclamations',  icon: <SupportAgentIcon />,  color: '#dc2626' },
  { id: 'leads',         icon: <ContactMailIcon />,   color: '#0891b2' },
  { id: 'activities',    icon: <EventNoteIcon />,     color: '#475569' },
];

// ─────────────────────────────────────────────────────────────────────────────
// CRM — Multilingual content (FR / EN / AR)
// ─────────────────────────────────────────────────────────────────────────────

export const CRM_CONTENT: AppManualContent = {
  fr: {
    dashboard: {
      title: 'Tableau de Bord Commercial & Prescription',
      description: 'Centre de pilotage décisionnel en temps réel dédié aux équipes commerciales, aux prescripteurs techniques et aux Key Account Managers (KAM). Permet d\'analyser le pipeline de vente, le suivi des objectifs et la rentabilité globale.',
      features: [
        'Sélecteur de profil dynamique : Vue Prescripteur, Vue KAM et Vue Direction Globale',
        'Indicateurs clés en direct : Volume d\'affaires pondéré, Taux de transformation global, Prescripteurs actifs, Chantiers en cours',
        'Barres de progression des objectifs mensuels avec calcul d\'atteinte en pourcentage',
        'Alertes prioritaires : Projets stagnants, chantiers nécessitant attention, réclamations critiques',
        'Cartes d\'action rapide pour créer une visite, enregistrer un cahier des charges ou déclarer une réclamation',
        'Statistiques de rentabilité moyenne et de satisfaction client'
      ],
      tips: [
        'Consultez le taux de rentabilité moyen pour prioriser les opportunités à forte marge.',
        'La vue Prescripteur isole les indicateurs en amont (Bureaux d\'études, CCTP), tandis que la vue KAM se concentre sur les entreprises générales et les commandes effectives.',
        'Les alertes de projets stagnants signalent les affaires sans activité depuis plus de 30 jours.'
      ],
      steps: [
        'Sélectionnez votre profil de travail (Prescripteur, KAM ou Global) via les boutons de filtre en haut.',
        'Analysez les métriques clés de performance et les taux d\'atteinte d\'objectifs.',
        'Examinez les alertes critiques pour identifier les actions correctives immédiates.',
        'Utilisez les boutons d\'accès rapide pour lancer vos opérations quotidiennes sans quitter l\'écran.'
      ]
    },
    organisations: {
      title: 'Écosystème Prescription & Organisations',
      description: 'Répertoire centralisé des acteurs de l\'écosystème de la construction : Bureaux d\'Études Techniques (BET), Cabinets d\'Architectes, Promoteurs Immobiliers, Maîtres d\'Ouvrage (MO), Entreprises Générales et Installateurs.',
      features: [
        'Double affichage intuitif : Vue Grille responsive avec cartes détaillées ou Vue Tableau tabulaire dense',
        'Typologie complète des partenaires (BET, ARCHITECTE, PROMOTEUR, MO, ENT, INSTALLATEUR, AUTRE)',
        'Classification par spécialité (Résidentiel, Tertiaire, Industriel, Mixte) et taille d\'organisation',
        'Historique relationnel, préférences techniques et projets prescrits antérieurs',
        'Coordonnées directes (Wilaya/Région, Adresse, Téléphone, Email, Site Web)',
        'Compteurs automatisés de projets associés et contacts rattachés'
      ],
      tips: [
        'Utilisez la vue Grille pour un aperçu visuel rapide des prescripteurs et la vue Tableau pour les recherches et tris multicritères.',
        'Renseignez soigneusement la zone géographique pour faciliter les tournées terrain par Wilaya.',
        'L\'historique relationnel permet à tout collaborateur de connaître instantanément les habitudes de prescription du partenaire.'
      ],
      steps: [
        'Cliquez sur le bouton "Nouveau" pour ouvrir le formulaire de création d\'une organisation.',
        'Renseignez la raison sociale, le type, la spécialité principale et la zone géographique.',
        'Ajoutez les coordonnées de contact et détaillez l\'historique relationnel et technique.',
        'Validez pour enregistrer la fiche dans la base de données et l\'écosystème synchronisé.'
      ]
    },
    contacts: {
      title: 'Contacts & Décideurs Clés',
      description: 'Gestion fine du carnet d\'adresses relationnel et des décideurs clés de chaque organisation ou entreprise cliente. Intègre l\'évaluation de l\'influence décisionnelle et les préférences de communication.',
      features: [
        'Rattachement hiérarchique à une organisation parente ou entreprise indépendante',
        'Évaluation de l\'influence décisionnelle sur une échelle visuelle de 1 à 5 étoiles',
        'Profilage de relation : Contact Principal, Technique, Commercial, Décideur, Administratif',
        'Préférences de communication (Email, Téléphone, WhatsApp, Réunion) et créneaux horaires privilégiés',
        'Préférence linguistique (Français, Arabe, Anglais) pour les échanges personnalisés',
        'Coordonnées complètes (Email, Téléphone, Mobile, Ville, Adresse)'
      ],
      tips: [
        'Classez les contacts 4 et 5 étoiles comme prioritaires dans vos plans de visites et invitations techniques.',
        'Enregistrez les créneaux horaires privilégiés pour maximiser l\'efficacité de vos relances téléphoniques.',
        'Associez systématiquement un contact à son organisation pour bénéficier du calcul automatique des statistiques.'
      ],
      steps: [
        'Créez un contact via le bouton "Nouveau" ou modifiez un contact existant.',
        'Liez-le à son organisation de rattachement via le menu déroulant.',
        'Spécifiez son rôle exact (ex: Chef de projet, Directeur technique, Acheteur).',
        'Attribuez son niveau d\'influence et ses coordonnées directes (téléphone, mobile, email).',
        'Enregistrez pour synchroniser la fiche contact.'
      ]
    },
    visits: {
      title: 'Visites Terrain & Géolocalisation',
      description: 'Outil de planification et de compte-rendu des tournées commerciales et de prescription sur le terrain. Intègre la capture GPS directe par bouton, la gestion des types d\'intervention et la checklist de conformité.',
      features: [
        'Géolocalisation GPS instantanée via l\'API navigateur (Bouton "Obtenir ma position actuelle")',
        'Typologie d\'intervention : Prospection, Prescription technique, Suivi de chantier, Réunion d\'études, Négociation, SAV',
        'Rôle du visiteur : Prescripteur technique ou Key Account Manager (KAM)',
        'Statuts du cycle de visite : Planifiée, Réalisée, Annulée, Reportée',
        'Compte-rendu détaillé des échanges, prochaines étapes et date de relance programmée',
        'Checklist de visite avec validation des objectifs terrain'
      ],
      tips: [
        'Cliquez sur les coordonnées GPS d\'une visite pour ouvrir l\'itinéraire direct dans Google Maps.',
        'Liez toujours une visite à une opportunité ou un chantier pour maintenir la traçabilité complète de l\'affaire.',
        'Passez le statut à "Réalisée" dès la fin de l\'entretien pour actualiser les métriques du tableau de bord.'
      ],
      steps: [
        'Planifiez une visite en spécifiant l\'organisation, le contact et la date/heure prévue.',
        'Sur le terrain, utilisez le bouton GPS pour géo-valider automatiquement votre présence sur site.',
        'Rédigez le compte-rendu des discussions et cochez les éléments de la checklist validés.',
        'Définissez la date de la prochaine relance et passez le statut à "Réalisée".'
      ]
    },
    cdc: {
      title: 'Cahiers des Charges & Prescriptions (CCTP)',
      description: 'Module stratégique dédié à l\'ancrage des solutions et gammes de profilés techniques dans les cahiers des charges (CCTP) des maîtres d\'ouvrage et bureaux d\'études, en amont des appels d\'offres.',
      features: [
        'Suivi des gammes de produits préconisées (Urbania, Futura, Dooria, Solutions mixtes)',
        'Cycle de vie de la prescription : Brouillon, Transmis, En révision, Intégré au CCTP, Validé, Rejeté',
        'Estimation des métrés linéaires et de la surface vitrée globale du projet',
        'Rattachement direct à l\'organisation prescriptrice (BET/Architecte) et au projet cible',
        'Enregistrement des variantes techniques, exigences acoustiques et thermiques'
      ],
      tips: [
        'Un CCTP verrouillé au stade "Intégré" confère un avantage concurrentiel décisif lors de la consultation des entreprises.',
        'Joignez les fiches d\'essais acoustiques et thermiques pour faciliter l\'adoption par l\'architecte.',
        'Assurez un suivi régulier avec le BET pendant la phase de révision du dossier d\'appel d\'offres.'
      ],
      steps: [
        'Ouvrez un nouveau dossier de prescription technique CCTP.',
        'Sélectionnez l\'organisation prescriptrice et le projet concerné.',
        'Choisissez la gamme technique adaptée (ex: Gamme coulissante Urbania ou ouvrant Futura).',
        'Estimez les métrés et spécifiez les clauses techniques de performance.',
        'Faites évoluer le statut vers "Intégré au CCTP" dès que le bureau d\'études valide la notice.'
      ]
    },
    opportunities: {
      title: 'Projets & Pipeline d\'Affaires',
      description: 'Gestion complète du cycle de vente des projets de menuiserie industrielle et bâtiment. Gère la progression dans le pipeline, les segments de marché (Promotion, AADL, LPP, Tertiaire), l\'évaluation du risque et la passation KAM.',
      features: [
        'Pipeline de vente en 6 étapes : Prospection, Qualification, Prescription CCTP, Proposition / Devis, Négociation, Gagné / Perdu',
        'Segments de marché ciblés : Promotion Immobilière, AADL, LPP, Tertiaire, Équipement Public, Logement Individuel',
        'Calcul financier automatique : Montant brut, Taux de remise accordé (%), Montant net et Valeur pondérée selon la probabilité',
        'Évaluation dynamique du niveau de risque du projet (Faible, Moyen, Élevé)',
        'Workflow de passation Prescripteur vers KAM : Statut de transfert (En attente, Transféré, Refusé) avec date et consigne d\'action requise'
      ],
      tips: [
        'La valeur pondérée (Montant × Probabilité) permet à la direction de fiabiliser les prévisions de chiffre d\'affaires.',
        'Une fois l\'affaire gagnée, basculez directement vers le module Chantiers pour lancer la phase d\'exécution.',
        'Utilisez le champ "Action requise" pour formaliser le passage de témoin entre le prescripteur et le KAM commercial.'
      ],
      steps: [
        'Créez une nouvelle opportunité d\'affaires en précisant le segment de marché et l\'organisation.',
        'Indiquez le montant estimé et la probabilité de succès (ex: 70% en phase de devis).',
        'Si la prescription est validée et que l\'entreprise adjudicataire est connue, activez le transfert KAM.',
        'Mettez à jour le stade au fur et à mesure des négociations jusqu\'au statut "Gagné".'
      ]
    },
    chantiers: {
      title: 'Chantiers Actifs & Suivi d\'Exécution',
      description: 'Supervision opérationnelle des chantiers signés en cours d\'exécution. Permet le suivi en temps réel de l\'avancement global, des cadences de livraison usine, de la pose sur site et de la santé financière (encaissements et marges).',
      features: [
        'Suivi des phases de chantier : Préparation, Démarrage, En cours, Pose, Réception, Clôturé, En pause',
        'Indicateur visuel d\'avancement en pourcentage (%) avec barre de progression dynamique',
        'Suivi des livraisons usine : Dans les délais, Livraison partielle, Livré, En retard',
        'Métriques financières : Montant total commandé, Taux d\'encaissement (%) et Marge brute estimée (%)',
        'Rattachement au projet d\'origine, à l\'organisation contractante et au responsable de site'
      ],
      tips: [
        'Un chantier dont le statut de livraison est "En retard" déclenche automatiquement une alerte sur le tableau de bord.',
        'Utilisez la vue tableau pour filtrer rapidement tous les chantiers d\'une même Wilaya.',
        'Vérifiez le taux d\'encaissement avant de valider l\'expédition des tranches suivantes de fabrication.'
      ],
      steps: [
        'Ouvrez un chantier actif depuis la liste ou transformez une opportunité gagnée.',
        'Actualisez régulièrement le pourcentage d\'avancement de la pose sur site.',
        'Contrôlez l\'état des livraisons usine pour anticiper les retards de chantier.',
        'Suivez les encaissements pour vous assurer du respect de l\'échéancier financier convenu.'
      ]
    },
    reclamations: {
      title: 'Réclamations SAV & Non-conformités',
      description: 'Gestion rigoureuse de la qualité et du service après-vente (SAV). Permet d\'enregistrer, d\'investiguer et de résoudre les anomalies constatées sur les produits ou leur mise en œuvre sur chantier.',
      features: [
        'Typologie des anomalies : Défaut produit usine, Erreur de pose / mise en œuvre, Retard de livraison, Non-conformité vitrage, Accessoires manquants',
        'Niveaux de sévérité gradués : Faible, Moyenne, Élevée, Bloquante (avec impact visuel prioritaire)',
        'Cycle de résolution : Ouverte, En investigation, En cours de traitement, Résolue, Clôturée',
        'Date de constatation, date de résolution effective et calcul du délai de clôture',
        'Historique des actions correctives engagées et solutions apportées'
      ],
      tips: [
        'Les réclamations de sévérité "Bloquante" doivent être traitées sous 24 à 48 heures pour éviter l\'arrêt du chantier.',
        'L\'analyse des types de réclamations permet d\'identifier les besoins en formation des équipes de pose.',
        'N\'oubliez pas de joindre la description précise de la non-conformité pour guider le diagnostic technique.'
      ],
      steps: [
        'Déclarez une nouvelle réclamation en sélectionnant le chantier et le contact déclarant.',
        'Précisez la nature exacte du défaut (produit, pose, vitrage) et le niveau de sévérité.',
        'Assignez les investigations techniques et passez le statut à "En cours".',
        'Une fois l\'intervention SAV réalisée, consignez la solution et clôturez le ticket.'
      ]
    },
    leads: {
      title: 'Pistes & Prospection Commerciale',
      description: 'Entonnoir de qualification initiale des opportunités brutes et contacts entrants. Permet d\'évaluer le potentiel commercial avant de convertir les pistes prometteuses en organisations et opportunités qualifiées.',
      features: [
        'Suivi des canaux d\'acquisition : Salons professionnels, Référencement web, Recommandation, Prospection directe, Réseaux sociaux',
        'Statuts d\'avancement : Nouveau, Contacté, Qualifié, Non qualifié, Converti',
        'Estimation de la valeur financière potentielle de la piste',
        'Prise de notes commerciales et traçabilité des premiers échanges',
        'Conversion fluide vers le carnet de contacts et le pipeline d\'opportunités'
      ],
      tips: [
        'Un lead qualifié rapidement dans les 24h a un taux de conversion supérieur de plus de 40%.',
        'Notez la source exacte pour mesurer le retour sur investissement des salons et campagnes de communication.',
        'Archivez en "Non qualifié" les contacts non pertinents pour garder une base commerciale saine.'
      ],
      steps: [
        'Enregistrez une nouvelle piste entrante avec ses coordonnées et sa source d\'acquisition.',
        'Prenez contact avec le prospect pour qualifier son besoin en menuiserie.',
        'Mettez à jour le statut en "Contacté" puis "Qualifié" si le projet est avéré.',
        'Créez ensuite l\'organisation et l\'opportunité pour démarrer le suivi dans le pipeline.'
      ]
    },
    activities: {
      title: 'Journal des Activités Commerciales',
      description: 'Registre d\'audit et de suivi de toutes les interactions quotidiennes entre l\'équipe commerciale et ses interlocuteurs. Assure la mémoire commerciale et la continuité du service client.',
      features: [
        'Typologie d\'activités : Appel téléphonique, Réunion technique, Email, Visite showroom, Déjeuner d\'affaires, Tâche interne',
        'Statuts d\'exécution : À faire, En cours, Terminée, Annulée',
        'Niveaux de priorité : Basse, Moyenne, Haute, Urgente',
        'Horodatage précis avec heure de début, durée et date d\'échéance',
        'Rattachement contextuel aux opportunités, contacts et organisations'
      ],
      tips: [
        'Créez systématiquement la prochaine tâche de relance dès la fin d\'un entretien téléphonique.',
        'Les activités en retard apparaissent en surbrillance pour vous aider à ne manquer aucun engagement client.',
        'Filtrez par statut "À faire" pour organiser efficacement votre journée de prospection.'
      ],
      steps: [
        'Créez une activité pour planifier un rappel téléphonique ou consigner une réunion tenue.',
        'Précisez l\'organisation, le contact et l\'objet de l\'échange.',
        'Fixez la date d\'échéance et le niveau de priorité.',
        'Après l\'échange, notez le résumé des discussions et cochez l\'activité comme "Terminée".'
      ]
    }
  },
  en: {
    dashboard: {
      title: 'Commercial & Prescription Dashboard',
      description: 'Real-time decision-making control center for sales teams, technical prescribers, and Key Account Managers (KAM). Enables pipeline analysis, target tracking, and overall profitability monitoring.',
      features: [
        'Dynamic profile selector: Prescriber View, KAM View, and Global Executive View',
        'Live key metrics: Weighted sales volume, Overall conversion rate, Active prescribers, Ongoing projects',
        'Monthly target progress bars with percentage completion calculation',
        'Priority alerts: Stagnant deals, projects requiring attention, critical claims',
        'Quick action cards to log a visit, register technical specs, or report a claim',
        'Average margin and customer satisfaction KPIs'
      ],
      tips: [
        'Review average profitability rates to focus effort on high-margin opportunities.',
        'Prescriber view isolates upstream design indicators (BET, specs), while KAM view concentrates on general contractors and executed orders.',
        'Stagnant project alerts highlight deals with no logged activity for over 30 days.'
      ],
      steps: [
        'Select your working profile (Prescriber, KAM, or Global) using top filter buttons.',
        'Analyze key performance indicators and target completion ratios.',
        'Review critical alerts to identify urgent corrective actions.',
        'Use quick action buttons to initiate daily operations directly from the dashboard.'
      ]
    },
    organisations: {
      title: 'Prescription Ecosystem & Organisations',
      description: 'Centralized directory of construction ecosystem stakeholders: Engineering Consultancies (BET), Architecture Firms, Property Developers, Project Owners (MO), General Contractors, and Installers.',
      features: [
        'Dual view modes: Responsive Card Grid or dense tabular data view',
        'Complete partner typology (BET, ARCHITECT, DEVELOPER, OWNER, CONTRACTOR, INSTALLER, OTHER)',
        'Classification by specialty (Residential, Commercial, Industrial, Mixed) and company size',
        'Relational history, technical preferences, and previously specified projects',
        'Direct contact details (Wilaya/Region, Full address, Phone, Professional email, Website)',
        'Automated counters for linked projects and associated decision-makers'
      ],
      tips: [
        'Use Grid view for rapid visual browsing of partners and Table view for multi-criteria sorting and export.',
        'Fill in the geographic region accurately to optimize field visit routes by Wilaya.',
        'Detailed relational history ensures team members understand partner preferences before meetings.'
      ],
      steps: [
        'Click the "New" button to open the organization creation form.',
        'Enter legal name, partner type, primary specialty, and geographic location.',
        'Provide contact details and document technical partnership history.',
        'Save to synchronize the partner across the CRM ecosystem.'
      ]
    },
    contacts: {
      title: 'Contacts & Key Decision Makers',
      description: 'Granular management of relational contacts and key decision-makers within each partner or client firm. Includes decision influence scoring and communication preferences.',
      features: [
        'Hierarchical link to parent organization or independent enterprise',
        'Visual decision influence rating from 1 to 5 stars',
        'Relational profiling: Primary Contact, Technical, Commercial, Decision Maker, Administrative',
        'Communication preferences (Email, Phone, WhatsApp, In-person meeting) and preferred time slots',
        'Language preference (French, Arabic, English) for tailored interactions',
        'Complete contact information (Email, Direct phone, Mobile, City, Address)'
      ],
      tips: [
        'Prioritize 4 and 5-star contacts when organizing technical presentations or executive meetings.',
        'Record preferred contact hours to maximize the success of follow-up calls.',
        'Always link contacts to their organization to enable aggregated reporting.'
      ],
      steps: [
        'Create a contact using the "New" button or open an existing contact to edit.',
        'Select the parent organization from the dropdown menu.',
        'Specify their exact title and role (e.g., Project Director, Chief Architect, Procurement Manager).',
        'Assign their influence rating and enter direct phone and email coordinates.',
        'Save to update the centralized contact directory.'
      ]
    },
    visits: {
      title: 'Field Visits & Geolocation',
      description: 'Planning and reporting tool for field sales and technical prescription visits. Integrates one-click GPS coordinate capture, visit typology, and compliance checklists.',
      features: [
        'Instant GPS geolocation via browser API ("Get My Current Location" button)',
        'Visit typology: Prospecting, Technical prescription, Job site inspection, Design meeting, Negotiation, After-sales',
        'Visitor role attribution: Technical Prescriber or Key Account Manager (KAM)',
        'Visit lifecycle stages: Planned, Completed, Cancelled, Postponed',
        'Detailed debrief notes, next steps, and scheduled follow-up dates',
        'Pre-flight checklist with verification of visit objectives'
      ],
      tips: [
        'Click on GPS coordinates to open the location directly in Google Maps for turn-by-turn navigation.',
        'Always link field visits to an opportunity or job site for full auditability.',
        'Mark visits as "Completed" immediately after the meeting to keep dashboard metrics up to date.'
      ],
      steps: [
        'Schedule a visit by selecting the target organization, contact, date, and time.',
        'On site, click the GPS button to timestamp and geolocate your physical presence.',
        'Record discussion highlights and mark completed checklist items.',
        'Set the next follow-up date and transition the status to "Completed".'
      ]
    },
    cdc: {
      title: 'Specifications & Prescriptions (CCTP)',
      description: 'Strategic module dedicated to locking proprietary profile systems and technical ranges into project specifications (CCTP) during early design phases before tenders.',
      features: [
        'Technical product range tracking (Urbania sliding, Futura casement, Dooria, Mixed systems)',
        'Prescription lifecycle: Draft, Transmitted, In Review, Locked into CCTP, Approved, Rejected',
        'Linear meter estimation and total glazing surface calculation',
        'Direct linkage to prescribing consultancy (BET/Architect) and target project',
        'Documentation of acoustic performance, thermal insulation, and project-specific variants'
      ],
      tips: [
        'Specifications locked into the tender CCTP provide a decisive edge when contractors bid.',
        'Attach technical test certificates to facilitate approval by consulting engineers.',
        'Schedule proactive review meetings during the architectural design phase.'
      ],
      steps: [
        'Open a new technical prescription file.',
        'Select the prescribing architectural firm and associated building project.',
        'Select the recommended product range (e.g., Urbania sliding or Futura casement system).',
        'Estimate linear meters and specify mandatory thermal/acoustic criteria.',
        'Promote status to "Locked into CCTP" once the engineering team validates specifications.'
      ]
    },
    opportunities: {
      title: 'Deals Pipeline & Projects',
      description: 'Full sales cycle management for industrial and building joinery projects. Handles pipeline stage progression, market segments (Private Dev, Social Housing, Commercial), risk assessment, and KAM handover.',
      features: [
        '6-stage sales pipeline: Prospecting, Qualification, CCTP Specification, Proposal/Quote, Negotiation, Closed Won/Lost',
        'Targeted market segments: Private Property Dev, Social Housing (AADL/LPP), Commercial, Public Infrastructure, Individual',
        'Automated financial calculator: Gross amount, Discount rate (%), Net value, and Probability-weighted amount',
        'Dynamic project risk level evaluation (Low, Medium, High)',
        'Prescriber to KAM handover workflow: Transfer status (Pending, Transferred, Rejected) with handover date and required actions'
      ],
      tips: [
        'Probability-weighted pipeline values allow executive leadership to forecast revenue accurately.',
        'Once a project reaches "Closed Won", immediately initiate the job site in the Active Sites module.',
        'Use the "Required Action" field to document specific handover instructions for the KAM.'
      ],
      steps: [
        'Create a new opportunity specifying the target segment, client organization, and expected close date.',
        'Input estimated deal value and probability percentage (e.g., 70% during proposal stage).',
        'When the tender is awarded to a general contractor, initiate the KAM handover workflow.',
        'Update stages as negotiations advance until securing the final contract.'
      ]
    },
    chantiers: {
      title: 'Active Sites & Execution Tracking',
      description: 'Operational supervision of ongoing executed projects. Enables real-time tracking of overall progress, factory delivery schedules, on-site installation rates, and financial health (collections and margins).',
      features: [
        'Site execution phases: Preparation, Kickoff, In Progress, Installation, Handover/Inspection, Closed, On Hold',
        'Visual progress indicator (%) with animated progress bars',
        'Factory supply logistics tracking: On Schedule, Partially Delivered, Delivered, Delayed',
        'Financial tracking: Total order value, Payment collection rate (%), and Gross profit margin (%)',
        'Direct link to parent opportunity, general contractor, and site supervisor'
      ],
      tips: [
        'Projects with "Delayed" supply status automatically generate priority warnings on the dashboard.',
        'Use the table filter to review all construction sites located within a specific Wilaya.',
        'Monitor collection rates closely before approving subsequent manufacturing batches.'
      ],
      steps: [
        'Open an active job site from the list or convert a won deal.',
        'Regularly update the on-site installation progress percentage.',
        'Track factory shipments to preempt logistical bottlenecks on site.',
        'Monitor incoming client disbursements against the contractual payment schedule.'
      ]
    },
    reclamations: {
      title: 'After-Sales Claims & Non-Conformities',
      description: 'Quality management and after-sales service (ASS) tracking. Record, investigate, and resolve defects observed on products or their on-site installation.',
      features: [
        'Defect categorization: Factory product defect, Installation error, Delivery delay, Glazing non-conformity, Missing hardware',
        'Graduated severity levels: Low, Medium, High, Blocking (with visual escalation badges)',
        'Resolution lifecycle: Open, Under Investigation, In Progress, Resolved, Closed',
        'Logged incident date, resolution date, and turnaround time tracking',
        'Comprehensive corrective action log and customer sign-off notes'
      ],
      tips: [
        'Tickets marked as "Blocking" severity must be triaged within 24 to 48 hours to prevent work stoppage.',
        'Analyzing root causes helps identify training opportunities for installation subcontractors.',
        'Always record detailed descriptions and photos of the non-conformity to expedite technical diagnosis.'
      ],
      steps: [
        'Log a new claim by selecting the impacted job site and reporting contact.',
        'Define defect type (product, installation, glazing) and select severity level.',
        'Assign a technical inspector and transition ticket status to "In Progress".',
        'Upon completing corrective work, document the resolution and close the ticket.'
      ]
    },
    leads: {
      title: 'Leads & Prospecting',
      description: 'Initial qualification funnel for incoming contacts and raw leads. Evaluate business potential before converting promising prospects into qualified organizations and deals.',
      features: [
        'Multi-channel acquisition tracking: Trade shows, Website inbound, Referral, Direct prospecting, Social media',
        'Lead qualification stages: New, Contacted, Qualified, Disqualified, Converted',
        'Estimated financial opportunity value',
        'Sales notes repository and audit log of initial discovery calls',
        'Seamless one-click conversion into organizations and deals pipeline'
      ],
      tips: [
        'Leads followed up within 24 hours yield a 40% higher conversion rate.',
        'Capture the acquisition channel accurately to compute marketing and trade show ROI.',
        'Disqualify irrelevant leads quickly to keep the active pipeline clean and actionable.'
      ],
      steps: [
        'Register a new incoming lead with coordinates and marketing origin.',
        'Initiate contact to assess building joinery requirements and project timelines.',
        'Advance status to "Contacted" and then "Qualified" once intent is confirmed.',
        'Convert the lead into an organization and opportunity to launch the sales cycle.'
      ]
    },
    activities: {
      title: 'Commercial Activities Log',
      description: 'Audit log and tracking repository for all daily interactions between sales staff and external partners. Ensures relational memory and seamless service continuity.',
      features: [
        'Activity typology: Phone call, Technical meeting, Email, Showroom visit, Business lunch, Internal task',
        'Execution status: To Do, In Progress, Completed, Cancelled',
        'Priority scoring: Low, Medium, High, Urgent',
        'Accurate timestamping with start time, duration, and due date',
        'Contextual association with specific deals, contacts, and organizations'
      ],
      tips: [
        'Always schedule the next follow-up activity before completing the current one.',
        'Overdue tasks appear highlighted to ensure no customer commitment is forgotten.',
        'Filter by "To Do" status to organize your daily calling and visit agenda.'
      ],
      steps: [
        'Create an activity to schedule an upcoming call or document a completed meeting.',
        'Link the activity to the corresponding organization, contact, and deal.',
        'Set due date, estimated duration, and priority level.',
        'After execution, record discussion summary and mark the task as "Completed".'
      ]
    }
  },
  ar: {
    dashboard: {
      title: 'لوحة القيادة التجارية والوصف',
      description: 'مركز تحكم لاتخاذ القرار في الوقت الفعلي مخصص للفرق التجارية، الواصفين التقنيين ومديري الحسابات الرئيسية. يتيح تحليل مسار المبيعات ومتابعة الأهداف والربحية العامة.',
      features: [
        'محدد ملف العمل الديناميكي: عرض الواصف، عرض مدير الحسابات (KAM)، والعرض العام للإدارة',
        'مؤشرات الأداء اللحظية: حجم الأعمال المرجح، معدل التحويل الكلي، الواصفون النشطون، والورشات الجارية',
        'أشرطة تقدم الأهداف الشهرية مع حساب نسبة الإنجاز المئوية',
        'تنبيهات ذات أولوية: المشاريع الراكدة، الورشات التي تتطلب متابعة، والشكاوى الحرجة',
        'بطاقات العمل السريع لتسجيل زيارة، إدراج دفتر شروط أو تقديم شكوى',
        'إحصائيات متوسط الهامش المالي ونسبة رضا العملاء'
      ],
      tips: [
        'راجع معدل الربحية لتوجيه الجهود نحو الفرص ذات الهامش الأعلى.',
        'يعزل عرض الواصف مؤشرات الدراسات الأولية (مكاتب الدراسات ودفاتر الشروط)، بينما يركز عرض KAM على المقاولات وطلبات الشراء الفعلية.',
        'تنبهك مؤشرات المشاريع الراكدة إلى الصفقات التي لم تشهد أي نشاط تجاري منذ أكثر من 30 يوماً.'
      ],
      steps: [
        'اختر ملف العمل المناسب (واصف، مدير حسابات، أو عام) عبر أزرار الفلترة في الأعلى.',
        'حلل مؤشرات الأداء الرئيسية ونسب تحقيق الأهداف المحددة.',
        'افحص التنبيهات الحرجة لاتخاذ الإجراءات التصحيحية الفورية.',
        'استخدم أزرار الوصول السريع لإطلاق مهامك اليومية دون مغادرة الشاشة.'
      ]
    },
    organisations: {
      title: 'منظومة الوصف والمؤسسات',
      description: 'دليل مركزي للجهات الفاعلة في قطاع البناء: مكاتب الدراسات الفنية (BET)، مكاتب الهندسة المعمارية، المرقون العقاريون، أصحاب المشاريع (MO)، الشركات العامة والقائمون بالتركيب.',
      features: [
        'عرض مزدوج ومرن: عرض شبكي بالبطاقات أو عرض جدولي مفصل',
        'تصنيف شامل للشركاء (مكتب دراسات، معمار، مرقي عقاري، صاحب مشروع، مقاولة، مركب، آخر)',
        'فرز حسب التخصص (سكني، تجاري/خدماتي، صناعي، مختلط) وحجم المؤسسة',
        'سجل تاريخ العلاقات، التفضيلات التقنية والمشاريع الموصوفة سابقاً',
        'بيانات الاتصال المباشرة (الولاية/المنطقة، العنوان، الهاتف، البريد الإلكتروني، الموقع)',
        'عدادات تلقائية للمشاريع المرتبطة وجهات الاتصال المسجلة'
      ],
      tips: [
        'استخدم العرض الشبكي للمعاينة السريعة للشركاء والعرض الجدولي للبحث والفرز المتقدم.',
        'حدد الولاية والمنطقة الجغرافية بدقة لتسهيل تخطيط الجولات الميدانية.',
        'تدوين تفضيلات الشريك في السجل التاريخي يساعد باقي الزملاء في فهم احتياجاته فوراً.'
      ],
      steps: [
        'انقر على زر "جديد" لفتح استمارة إنشاء مؤسسة جديدة.',
        'أدخل الاسم التجاري، نوع الشريك، التخصص الرئيسي والمنطقة الجغرافية.',
        'أضف بيانات الاتصال وسجل التاريخ التقني والتعاملي مع المؤسسة.',
        'احفظ لإدراج المؤسسة في قاعدة البيانات ومنظومة العمل المتزامنة.'
      ]
    },
    contacts: {
      title: 'جهات الاتصال وصناع القرار',
      description: 'إدارة دقيقة لجهات الاتصال وصناع القرار داخل كل مؤسسة أو شركة عميلة. تشمل تقييم مستوى التأثير في القرار وتفضيلات التواصل.',
      features: [
        'ربط هرمي بالمؤسسة التابعة أو الشركة المستقلة',
        'تقييم مرئي لدرجة التأثير في القرار من 1 إلى 5 نجوم',
        'تحديد طبيعة العلاقة: اتصال رئيسي، تقني، تجاري، صانع قرار، إداري',
        'تفضيلات قنوات التواصل (بريد إلكتروني، هاتف، واتساب، اجتماعات) والأوقات المفضلة',
        'تفضيل اللغة (فرنسية، عربية، إنجليزية) لتبادل المراسلات بمهنية',
        'بيانات الاتصال الكاملة (الهاتف المباشر، المحمول، البريد، العنوان)'
      ],
      tips: [
        'امنح الأولوية لجهات الاتصال الحائزة على 4 و 5 نجوم عند ترتيب العروض واللقاءات التقنية.',
        'سجل الأوقات المفضلة للاتصال لزيادة فاعلية التذكيرات والمكالمات.',
        'اربط كل جهة اتصال بمؤسستها لضمان دقة الإحصائيات الشاملة.'
      ],
      steps: [
        'أنشئ جهة اتصال جديدة عبر زر "جديد" أو افتح جهة اتصال للتعديل.',
        'اختر المؤسسة التابعة لها من القائمة المنسدلة.',
        'حدد المسمى الوظيفي والدور الدقيق (مثل: مدير المشروع، المدير الفني، مسؤول المشتريات).',
        'حدد درجة التأثير وأدخل بيانات الهاتف والبريد الإلكتروني المباشرة.',
        'احفظ لتحديث السجل المركزي لجهات الاتصال.'
      ]
    },
    visits: {
      title: 'الزيارات الميدانية وتحديد المواقع',
      description: 'أداة لتخطيط وتدوين تقارير الجولات الميدانية للفرق التجارية والواصفين. تتضمن الالتقاط المباشر لإحداثيات GPS وإدارة أنواع الزيارات وقوائم التحقق.',
      features: [
        'تحديد جغرافي فوري عبر إحداثيات GPS بنقرة واحدة ("الحصول على موقعي الحالي")',
        'تصنيف الزيارات: تنقيب، وصف فني، متابعة ورشة، اجتماع دراسات، تفاوض، ما بعد البيع',
        'تحديد دور الزائر: واصف فني أو مدير حسابات رئيسية (KAM)',
        'مراحل الزيارة: مبرمجة، منجزة، ملغاة، مؤجلة',
        'محضر تفصيلي للنقاشات، الخطوات القادمة وتاريخ المتابعة المقبل',
        'قائمة تحقق ميدانية للتأكد من استيفاء أهداف الزيارة'
      ],
      tips: [
        'انقر على إحداثيات GPS لفتح المسار الجغرافي مباشرة في خرائط Google.',
        'اربط الزيارة دائماً بفرصة تجارية أو ورشة عمل لضمان التتبع الكامل للصفقة.',
        'حول حالة الزيارة إلى "منجزة" فور انتهاء اللقاء لتحديث لوحة القيادة اللحظية.'
      ],
      steps: [
        'برمج زيارة باختيار المؤسسة، جهة الاتصال والتاريخ والتوقيت المحدد.',
        'في الميدان، استخدم زر GPS لتأكيد حضورك الفعلي في الموقع.',
        'سجل ملخص المحادثات وقم بالتأشير على عناصر قائمة التحقق المستوفاة.',
        'حدد موعد المتابعة القادم وحول الحالة إلى "منجزة".'
      ]
    },
    cdc: {
      title: 'دفاتر الشروط والمواصفات الفنية (CCTP)',
      description: 'وحدة استراتيجية مخصصة لإدراج وتثبيت الحلول ومجموعات المقاطع التقنية ضمن دفاتر الشروط والمواصفات الفنية (CCTP) للمشاريع قبل طرح المناقصات.',
      features: [
        'متابعة مجموعات المنتجات المعتمدة (أنظمة Urbania المنزلقة، أنظمة Futura المفصلية، أنظمة Dooria، والأنظمة المختلطة)',
        'دورة حياة الوصف الفني: مسودة، مرسل، قيد المراجعة، مدمج في دفتر الشروط، معتمد، مرفوض',
        'تقدير الأمتار الطولية وإجمالي المساحات الزجاجية للمشروع',
        'ربط مباشر بالمؤسسة الواصفة (مكتب الدراسات أو المهندس المعماري) والمشروع المستهدف',
        'تسجيل المتطلبات الصوتية والعزل الحراري والبدائل التقنية'
      ],
      tips: [
        'تثبيت النظام في دفتر الشروط يمنح أفضلية حاسمة عند استشارة المقاولات.',
        'أرفق شهادات الاختبارات الحرارية والصوتية لتسهيل اعتماد الحل لدى المهندس المعماري.',
        'حافظ على تواصل دوري مع مكتب الدراسات طوال مرحلة إعداد المناقصة.'
      ],
      steps: [
        'افتح ملف وصف فني جديد لدفتر الشروط CCTP.',
        'اختر مكتب الهندسة المعمارية الواصف والمشروع المعني.',
        'حدد مجموعة المقاطع والمنتجات الملائمة للمشروع.',
        'قدر الأمتار الطولية وحدد معايير الأداء والعزل المطلوبة.',
        'حول الحالة إلى "مدمج في دفتر الشروط" فور موافقة مهندس المشروع.'
      ]
    },
    opportunities: {
      title: 'المشاريع ومسار الصفقات',
      description: 'إدارة شاملة لدورة مبيعات مشاريع النجارة الصناعية والبناء. متابعة مراحل المسار، قطاعات السوق، تقييم المخاطر، والتسليم لمدير الحسابات الرئيسية.',
      features: [
        'مسار مبيعات في 6 مراحل: تنقيب، تأهيل، وصف فني CCTP، عرض أسعار، تفاوض، فوز / خسارة',
        'قطاعات السوق المستهدفة: الترقية العقارية، سكنات عدل (AADL)، سكنات LPP، قطاع الخدمات، المنشآت العامة، والسكن الفردي',
        'حاسبة مالية آلية: المبلغ الإجمالي، نسبة التخفيض، القيمة الصافية والمبلغ المرجح حسب احتمالية النجاح',
        'تقييم ديناميكي لمستوى مخاطر المشروع (منخفض، متوسط، مرتفع)',
        'آلية تسليم الصفقة من الواصف إلى مدير الحسابات (KAM): حالة التحويل (قيد الانتظار، تم التحويل، مرفوض) مع التاريخ والتوجيهات'
      ],
      tips: [
        'القيمة المرجحة (المبلغ × نسبة النجاح) تمكن الإدارة من وضع توقعات مالية دقيقة للإيرادات.',
        'عند الفوز بالصفقة، انتقل مباشرة إلى وحدة الورشات لبدء المتابعة التنفيذية.',
        'استخدم خانة "الإجراء المطلوب" لتوضيح توجيهات التسليم إلى مدير الحسابات بدقة.'
      ],
      steps: [
        'أنشئ فرصة تجارية جديدة وحدد قطاع السوق والمؤسسة الراعية للمشروع.',
        'أدخل القيمة المالية التقديرية ونسبة الاحتمالية (مثل: 70% في مرحلة العرض).',
        'عند اعتماد الوصف ومعرفة المقاولة المنفذة، قم بتفعيل تسليم الملف لمدير الحسابات.',
        'قم بتحديث المراحل مع تقدم المفاوضات حتى الوصول إلى مرحلة "فوز بالصفقة".'
      ]
    },
    chantiers: {
      title: 'الورشات النشطة ومتابعة التنفيذ',
      description: 'إشراف تشغيلي على المشاريع الموقعة قيد التنفيذ. يتيح المتابعة اللحظية لنسبة التقدم، وتيرة التسليم من المصنع، عمليات التركيب في الموقع، والوضع المالي للورشة.',
      features: [
        'مراحل إنجاز الورشة: تحضير، انطلاق، قيد الإنجاز، تركيب، استلام، منتهية، متوقفة مؤقتاً',
        'مؤشر مرئي لنسبة التقدم (%) مع شريط تقدم تفاعلي',
        'تتبع توريدات المصنع: في الموعد، تسليم جزئي، تم التسليم بالكامل، متأخر',
        'مؤشرات مالية دقيقة: القيمة الإجمالية للطلبية، نسبة التحصيل المالي (%)، وهامش الربح التقديري (%)',
        'ربط بالمشروع الأصلي، المقاولة المتعاقدة والمسؤول الميداني'
      ],
      tips: [
        'تأخر توريدات المصنع ينشئ تنبيهاً تلقائياً ذو أولوية في لوحة القيادة لتفادي تعطيل الورشة.',
        'استخدم العرض الجدولي لفرز ورشات العمل الواقعة في ولاية محددة بسهولة.',
        'تأكد من نسب التحصيل المالي قبل التصريح ببدء تصنيع الدفعات اللاحقة.'
      ],
      steps: [
        'افتح ورشة عمل من القائمة أو قم بتحويل فرصة تجارية تم الفوز بها.',
        'حدث نسبة تقدم أعمال التركيب في الورشة بشكل دوري.',
        'راقب حالة توريدات المصنع للتنبؤ بأي عوائق لوجستية قبل حدوثها.',
        'تابع المدفوعات المستلمة لضمان التزام المقاولة بالجدول المالي المتفق عليه.'
      ]
    },
    reclamations: {
      title: 'شكاوى ما بعد البيع وعدم المطابقة',
      description: 'إدارة الجودة وخدمة ما بعد البيع (SAV). تسجيل، التحقيق وحل المشكلات المتعلقة بالمنتجات أو طريقة تركيبها في مواقع البناء.',
      features: [
        'تصنيف العيوب: عيب في تصنيع المنتج، خطأ في التركيب، تأخر في التوريد، عدم مطابقة الزجاج، نقص في الملحقات',
        'مستويات خطورة متدرجة: منخفضة، متوسطة، عالية، وحرجة/معطلة للورشة',
        'مراحل معالجة الشكوى: مفتوحة، قيد التحقيق، قيد المعالجة، تم الحل، مغلقة',
        'تسجيل تاريخ المعاينة، تاريخ الحل الفعلي، وحساب مدة إغلاق الشكوى',
        'سجل تفصيلي للإجراءات التصحيحية المتخذة وملاحظات اعتماد العميل'
      ],
      tips: [
        'الشكاوى ذات الخطورة "الحرجة" يجب التدخل فيها خلال 24 إلى 48 ساعة لمنع توقف الورشة.',
        'تحليل أسباب الشكاوى يساعد في تحديد احتياجات تدريب فرق التركيب الميدانية.',
        'أرفق دائماً شرحاً دقيقاً لعدم المطابقة لتسهيل التشخيص الفني لدى المصنع.'
      ],
      steps: [
        'سجل شكوى جديدة باختيار الورشة المتأثرة وجهة الاتصال صاحبة البلاغ.',
        'حدد نوع العيب بدقة (منتج، تركيب، زجاج) ومستوى خطورته.',
        'عين المشرف الفني المعني بالمعاينة وحول الحالة إلى "قيد المعالجة".',
        'بعد إتمام التدخل، سجل الحل المعتمد وقم بإغلاق تذكرة الشكوى.'
      ]
    },
    leads: {
      title: 'الفرص المحتملة والتنقيب',
      description: 'مسار التأهيل الأولي لجهات الاتصال والفرص الجديدة. تقييم الإمكانات التجارية قبل تحويل الفرص المؤكدة إلى مؤسسات ومشاريع معتمدة.',
      features: [
        'تتبع قنوات استقطاب العملاء: المعارض المهنية، الموقع الإلكتروني، التوصيات، التنقيب الميداني، شبكات التواصل',
        'مراحل التأهيل: جديدة، تم التواصل، مؤهلة، غير مؤهلة، محولة',
        'تقدير القيمة المالية المحتملة للمشروع',
        'سجل الملاحظات والاتصالات التمهيدية الأولى مع العميل',
        'تحويل سلس بنقرة واحدة إلى دليل المؤسسات ومسار الصفقات'
      ],
      tips: [
        'المتابعة السريعة للفرصة خلال أول 24 ساعة ترفع نسبة تحويلها بنسبة تفوق 40%.',
        'سجل مصدر الفرصة بدقة لقياس العائد على استثمار المعارض والحملات التسويقية.',
        'استبعد الفرص غير المناسبة بسرعة للحفاظ على نقاء وفاعلية مسار المبيعات.'
      ],
      steps: [
        'سجل بيانات الفرصة الجديدة مع معلومات الاتصال ومصدر الاستقطاب.',
        'تواصل مع العميل لتحديد احتياجاته الدقيقة وجدول المشروع الزمني.',
        'حدث الحالة إلى "تم التواصل" ثم إلى "مؤهلة" عند تأكد جدية الطلب.',
        'قم بتحويل الفرصة إلى مؤسسة ومشروع لبدء دورة البيع الرسمية.'
      ]
    },
    activities: {
      title: 'سجل الأنشطة التجارية',
      description: 'سجل للمتابعة والتدقيق لجميع التفاعلات اليومية بين الفريق التجاري والشركاء الخارجيين، مما يضمن استمرارية الذاكرة التجارية وجودة الخدمة.',
      features: [
        'أنواع الأنشطة: مكالمة هاتفية، اجتماع فني، بريد إلكتروني، زيارة صالة العرض، غداء عمل، مهمة داخلية',
        'حالات التنفيذ: قيد الانتظار، جارية، منجزة، ملغاة',
        'مستويات الأولوية: منخفضة، متوسطة، عالية، عاجلة',
        'توقيت دقيق مع وقت البدء، المدة وتاريخ الاستحقاق',
        'ربط سياقي مباشر بالصفقات والورشات وجهات الاتصال والمؤسسات'
      ],
      tips: [
        'بادر دائماً بجدولة مهمة المتابعة التالية فور انتهائك من أي مكالمة أو لقاء.',
        'المهام المتأخرة تبرز بلون تحذيري لتذكيرك بالوفاء بالتزاماتك مع العملاء.',
        'استخدم فلتر المهام "قيد الانتظار" لتنظيم جدول أعمالك اليومي بكفاءة.'
      ],
      steps: [
        'أنشئ نشاطاً لبرمجة مكالمة قادمة أو لتدوين محضر اجتماع تم عقده.',
        'اربط النشاط بالمؤسسة وجهة الاتصال والمشروع المتعلق به.',
        'حدد تاريخ الاستحقاق، المدة المتوقعة ودرجة الأولوية.',
        'بعد التنفيذ، دون ملخص ما تم الاتفاق عليه واجعل النشاط "منجزاً".'
      ]
    }
  }
};
